import { useEffect, useMemo, useRef, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import useActions from '@app/store/hooks/useActions';
import { useGoogleCallbackMutation } from '@entities/auth';
import LoadingSpinner from '@shared/components/LoadingSpinner';

const GoogleCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setHasLoggedAs } = useActions();

  const [error, setError] = useState<string | null>(null);
  const [handleCallback, { isLoading, isError }] = useGoogleCallbackMutation();

  const paramsString = useMemo(() => searchParams.toString(), [searchParams]);
  const params = useMemo(() => {
    const entries: Record<string, string> = {};

    new URLSearchParams(paramsString).forEach((value, key) => {
      entries[key] = value;
    });

    return entries;
  }, [paramsString]);

  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (error || hasTriggeredRef.current) {
      return;
    }

    if (!Object.keys(params).length) {
      navigate('/admin', { replace: true });
      return;
    }

    hasTriggeredRef.current = true;

    const processCallback = async () => {
      try {
        const data = await handleCallback(params).unwrap();

        if (data.token) {
          setToken(data);
          if (params.as) {
            setHasLoggedAs(params.as);
          }
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        setError('Something went wrong while finishing Google sign-in.');
        console.error('Google callback error:', err);
      }
    };

    processCallback();
  }, [error, handleCallback, navigate, params, setHasLoggedAs, setToken]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || error) {
    return (
      <div>
        Something went wrong while finishing Google sign-in., try to{' '}
        <Link to='/admin'>login again here</Link>
      </div>
    );
  }

  return <LoadingSpinner />;
};

export default GoogleCallbackPage;
